/**
 * Preserve list context when drilling into hotel reports (e.g. /properties → PMS reports).
 * Also used to send unauthenticated deep links back after login.
 */

export const DEFAULT_POST_LOGIN_PATH = '/dashboard';

/** Set on explicit logout so the next sign-in does not resume the previous user's page. */
const SKIP_LOGIN_RETURN_KEY = 'zra.skipLoginReturnTo';

export function suppressPostLoginReturnTo() {
  try {
    sessionStorage.setItem(SKIP_LOGIN_RETURN_KEY, '1');
  } catch {
    /* sessionStorage can be unavailable in private mode */
  }
}

export function shouldIgnoreLoginReturnTo() {
  try {
    return sessionStorage.getItem(SKIP_LOGIN_RETURN_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearSuppressPostLoginReturnTo() {
  try {
    sessionStorage.removeItem(SKIP_LOGIN_RETURN_KEY);
  } catch {
    /* ignore */
  }
}

const AUTH_ONLY_PATHS = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
]);

/**
 * Same-origin relative path only (no protocol-relative or external URLs).
 * @param {unknown} value
 * @returns {string|null}
 */
export function safeInternalPath(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (!v.startsWith('/') || v.startsWith('//')) return null;
  if (v.includes('\\') || v.length > 2048) return null;
  const pathOnly = v.split(/[?#]/, 1)[0];
  if (AUTH_ONLY_PATHS.has(pathOnly)) return null;
  return v;
}

/**
 * @param {{ state?: unknown } | null | undefined} location react-router location
 * @returns {string|null}
 */
export function readReturnTo(location) {
  const state = location?.state;
  if (!state || typeof state !== 'object') return null;
  const v = /** @type {{ returnTo?: unknown }} */ (state).returnTo;
  return safeInternalPath(v);
}

/**
 * @param {{ pathname?: string; search?: string; hash?: string } | null | undefined} location
 */
export function currentReturnTo(location) {
  const pathname = location?.pathname ?? '';
  const search = location?.search ?? '';
  const hash = location?.hash ?? '';
  return `${pathname}${search}${hash}`;
}

/** @param {string|null|undefined} returnTo */
export function linkStateWithReturnTo(returnTo) {
  const safe = safeInternalPath(returnTo);
  if (!safe) return undefined;
  return { returnTo: safe };
}

/**
 * @param {string} pathname
 * @param {string|null|undefined} returnTo
 */
export function pathWithReturnTo(pathname, returnTo) {
  const safe = safeInternalPath(returnTo);
  if (!safe) return pathname;
  return `${pathname}?returnTo=${encodeURIComponent(safe)}`;
}

/** @param {string|null|undefined} returnTo */
export function loginPathWithReturnTo(returnTo) {
  return pathWithReturnTo('/login', returnTo);
}

/**
 * Where to send someone whose session just ended.
 * Explicit logout goes to a clean /login. A deep link opened while signed out keeps returnTo.
 * @param {{ pathname?: string; search?: string; hash?: string } | null | undefined} location
 */
export function loginDestinationAfterAuthLoss(location) {
  if (shouldIgnoreLoginReturnTo()) return '/login';
  return loginPathWithReturnTo(currentReturnTo(location));
}

/**
 * Deep-link target after login: location state, then ?returnTo= / ?from=.
 * @param {{ state?: unknown; search?: string } | null | undefined} location
 * @returns {string|null}
 */
export function readPostLoginRedirect(location) {
  const fromState = readReturnTo(location);
  if (fromState) return fromState;
  const search = location?.search;
  if (typeof search !== 'string' || search.length < 2) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return safeInternalPath(params.get('returnTo') || params.get('from'));
}

/**
 * @param {{ state?: unknown } | null | undefined} location
 * @param {string} fallback
 */
export function resolveBackPath(location, fallback) {
  return readReturnTo(location) ?? fallback;
}

/**
 * @param {{ state?: unknown; search?: string } | null | undefined} location
 * @param {string} [fallback]
 */
export function resolvePostLoginPath(location, fallback = DEFAULT_POST_LOGIN_PATH) {
  return readPostLoginRedirect(location) ?? fallback;
}
